import { isFunction } from 'lodash';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { doRedoColor, doUndoColor, enableChangeColor, clearColorOnly } from '../js/colorHandling';
import { doRedoPattern, doUndoPattern, patternHandling, clearPatternOnly } from '../js/patternHandling';
import { doRedoStyle, doUndoStyle } from '../js/styleHandling';
import { doRedoText, doUndoText } from '../js/text/textUndoRedoUtils';
import { doRedoLogo, doUndoLogo } from '../js/undoRedoLogo';
import {
  cameraCenter,
  clearAll,
  mesh,
  resizeFullView,
  restoreFullView,
  toolbarActive,
} from '../js/webGL';
import { setShow } from '../redux-tool/slice/controlSlice';
import { setSelectedStyleID } from '../redux-tool/slice/toolSlice';
import { toggleIsReview, toggleIsShowSharing } from '../redux-tool/slice/uiSlice';
import './styles/Toolbar.css';

export function ToolBar() {
  const urlParams = new URLSearchParams(window.location.search);
  const dispatch = useDispatch();
  const { isShowMenuToolbar, isReview, activeTabBarId, isShowSharing } = useSelector(
    (state) => state.ui,
  );
  const { isAddingText } = useSelector((state) => state.tool);
  const [open, setOpen] = useState(false);
  const [heightSubmitDesignArea, setSubmitDesignAreaHeight] = useState(314);
  const [heightToolBarArea, setToolBarHeight] = useState(36);
  const [isFlutterInAppWebViewReady, setIsFlutterInAppWebViewReady] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [canClear, setCanClear] = useState(false);

  useEffect(() => {
    dispatch(setShow(false));
    if (isReview) {
      if (isFunction(resizeFullView)) resizeFullView();
      toolbarActive.value = '';
      patternHandling.selectedPattern(null);
      window.currentPattern = null;
      enableChangeColor(false);
    } else {
      if (isFunction(resizeFullView)) restoreFullView();
      if (activeTabBarId === 0) {
        toolbarActive.value = 'logo';
      } else if (activeTabBarId === 1) {
        toolbarActive.value = 'text';
      } else if (activeTabBarId === 2) {
        toolbarActive.value = 'style';
      } else       if (activeTabBarId === 3) {
        toolbarActive.value = 'color';
        enableChangeColor(true);
        // Clear window.currentPattern to prevent applying pattern in color mode
        window.currentPattern = null;
      } else if (activeTabBarId === 4) {
        toolbarActive.value = 'pattern';
        enableChangeColor(false);
      }
    }
  }, [isReview, activeTabBarId]);

  const handleOpen = () => {
    dispatch(setShow(false));
    setOpen(true);
  };

  const handleClose = () => {
    window.isShareYourDesign = false;
    setOpen(false);
  };

  const onReview = () => {
    dispatch(toggleIsReview());
  };

  const handleShare = () => {
    cameraCenter();
    handleOpen();
    dispatch(toggleIsShowSharing());
  };

  const handleListener = () => {
    setIsFlutterInAppWebViewReady(true);
  };

  const handleClearAllAction = () => {
    const type = toolbarActive.value;
    if (type === 'pattern') {
      clearPatternOnly();
      // Keep window.currentLogo so user can apply pattern again
    } else if (type === 'color') {
      clearColorOnly(mesh);
    } else {
      clearAll();
      dispatch(setSelectedStyleID(window.currentStyle.Id));
    }
  };

  const handleUndo = () => {
    window.showControl(false, 0, 0);
    const type = toolbarActive.value;
    switch (type) {
      case 'pattern':
        doUndoPattern();
        break;
      case 'style':
        doUndoStyle();
        dispatch(setSelectedStyleID(window.currentStyle.Id));
        break;
      case 'color':
        doUndoColor();
        break;
      case 'logo':
        doUndoLogo();
        break;
      case 'text':
        doUndoText();
        break;
      default:
        break;
    }
  };

  const handleRedo = () => {
    const type = toolbarActive.value;
    window.showControl(false, 0, 0);
    switch (type) {
      case 'pattern':
        doRedoPattern();
        break;
      case 'style':
        doRedoStyle();
        dispatch(setSelectedStyleID(window.currentStyle.Id));
        break;
      case 'color':
        doRedoColor();
        break;
      case 'logo':
        doRedoLogo();
        break;
      case 'text':
        doRedoText();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    window.setUndoState = (value) => {
      setCanUndo(value);
    };
    window.setRedoState = (value) => {
      setCanRedo(value);
    };
    window.setClearState = (value) => {
      setCanClear(value);
    };

    // Manually check if ready (in case iOS returns do not replay the event)
    if (window.flutter_inappwebview) {
      handleListener();
    }

    window.addEventListener('flutterInAppWebViewPlatformReady', handleListener);
    const submitEl = document.getElementById('submit-custom-design');
    const toolbarEl = document.getElementsByClassName('toolbar-container')[0];
    if (toolbarEl && submitEl) {
      setSubmitDesignAreaHeight(submitEl.clientHeight);
      setToolBarHeight(toolbarEl.clientHeight);
    }

    return () => {
      setIsFlutterInAppWebViewReady(false);
      window.removeEventListener('flutterInAppWebViewPlatformReady', handleListener);
    };
  }, []);

  return (
    !isAddingText && (
      <div
        style={{
          height: '32px',
          width: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 2,
        }}
      >
        <div className="toolbar-container">
          {isShowSharing && (
            <div className="toolbar-container-next-btn-wrapper">
              <Button
                variant="info"
                size="sm"
                className="toolbar-container-next-btn"
                onClick={() => {
                  if (isFlutterInAppWebViewReady) {
                    window?.flutter_inappwebview &&
                      window?.flutter_inappwebview?.callHandler('DesignHub', 'onNextButtonClicked');
                  }
                }}
              >
                Next
              </Button>
            </div>
          )}
          {isShowMenuToolbar && (
            <div className="toolbar">
              {isFlutterInAppWebViewReady || isReview ? (
                <div className="icon-back-container">
                  <div
                    className="iconBack"
                    onClick={() => {
                      if (urlParams.has('decal') || !isReview) {
                        if (isFlutterInAppWebViewReady) {
                          window?.flutter_inappwebview &&
                            window?.flutter_inappwebview?.callHandler(
                              'DesignHub',
                              'onBackButtonClicked',
                            );
                        }
                      } else {
                        onReview();
                      }
                    }}
                  >
                    <img src="/theme/img/arrowLeft.svg" alt="arrowLeft"></img>
                  </div>
                </div>
              ) : (
                <div className="icon-back-container" />
              )}

              <div className="tool-icon-container" style={{ display: isReview ? 'none' : 'flex' }}>
                <div className="iconTool" onClick={handleClearAllAction}>
                  <img
                    src={
                      canClear
                        ? '/theme/img/control-icon/clear-active.svg'
                        : '/theme/img/control-icon/clear-inactive.svg'
                    }
                    alt="iconLogo"
                  ></img>
                </div>
                <div
                  className="iconTool"
                  onClick={() => {
                    if (toolbarActive.value === 'logo' || toolbarActive.value === 'pattern' || toolbarActive.value === 'color') {
                      handleUndo();
                    }
                  }}
                >
                  <img
                    src={
                      canUndo && (toolbarActive.value === 'logo' || toolbarActive.value === 'pattern' || toolbarActive.value === 'color')
                        ? '/theme/img/control-icon/undo-active.svg'
                        : '/theme/img/control-icon/undo-inactive.svg'
                    }
                    alt="iconLogo"
                  ></img>
                </div>
                <div
                  className="iconTool"
                  onClick={() => {
                    if (toolbarActive.value === 'logo' || toolbarActive.value === 'pattern' || toolbarActive.value === 'color') {
                      handleRedo();
                    }
                  }}
                >
                  <img
                    src={
                      canRedo && (toolbarActive.value === 'logo' || toolbarActive.value === 'pattern' || toolbarActive.value === 'color')
                        ? '/theme/img/control-icon/redo-active.svg'
                        : '/theme/img/control-icon/redo-inactive.svg'
                    }
                    alt="iconLogo"
                  ></img>
                </div>

                {/* <div className="iconTool" id="delete" style={{ display: "none" }}>
              <img src="/theme/img/delete.svg" alt="iconLogo"></img>
            </div> */}
                <div className="iconTool" onClick={onReview}>
                  <img src="/theme/img/control-icon/preview.svg" alt="iconLogo"></img>
                </div>
              </div>

              {isReview && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <p
                    style={{
                      color: '#FFF',
                      fontSize: '18px',
                      lineHeight: '24px',
                      fontWeight: '600',
                      margin: 0,
                    }}
                  >
                    View My Design
                  </p>
                </div>
              )}

              {!isReview ? (
                <div className="button-submit-container" onClick={handleOpen}>
                  <Button
                    variant="info"
                    size="sm"
                    style={{
                      borderRadius: '15px',
                      color: '#fdfdfd',
                      background: '#29f7f5',
                      fontWeight: '700',
                    }}
                    onClick={() => {
                      window.isShareYourDesign = true;
                    }}
                  >
                    Submit
                  </Button>
                </div>
              ) : (
                <div style={{ width: '76px' }} />
              )}
            </div>
          )}
          <div
            style={
              isShowMenuToolbar && open
                ? {
                    height: `calc(100vh - ${heightSubmitDesignArea}px + ${heightToolBarArea}px)`,
                  }
                : {}
            }
            className={`${isShowMenuToolbar && open ? 'overlay' : ''}`}
          />
          <div
            id="submit-custom-design"
            className={`bottom-sheet ${isShowMenuToolbar && open ? 'show' : ''}`}
          >
            <div className="sheet-header">
              <span className="sheet-drag"></span>
            </div>
            <div className="sheet-content">
              <p className="sheet-content-title">Submit Your Design?</p>
              <p className="sheet-content-area">You’re about to submit your design.</p>
              <p className="sheet-content-area">
                Please note that your submission is considered a concept and our Regna team will
                review and adjust it to meet production standards.
              </p>
              <p className="sheet-content-area">
                We’ll notify you when the final version is ready for your approval.
              </p>
              <div className="sheet-content-action">
                <div className="continue-edit" onClick={handleClose}>
                  Continue editing
                </div>
                <div className="submit-btn" onClick={handleShare}>
                  Submit
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
}
